import type { SoftphoneStatus } from "./SoftphoneProvider";

export type CallTone = "active" | "ended" | "error";

export interface CallDisplay {
  // Primary status line on the card / pill (the live mm:ss timer for in-call).
  statusLine: string;
  // Secondary copy shown under the status line (error states only).
  detail?: string;
  tone: CallTone;
  // Avatar accent: blue while the call is live, grey once it has ended.
  isLive: boolean;
  isTerminal: boolean;
  showMute: boolean; // mute/unmute toggle (in-call only)
  showHangup: boolean; // end-call button (live call)
  showAnswer: boolean; // answer/decline pair (incoming ring, CRM-905)
}

function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Single source of truth for what the call card and the minimized pill render
 * for a given softphone status. Mirrors the Figma in-call states (CRM-811 +
 * the CRM-905 incoming ring): Incoming call / Ringing → mm:ss (with mute) →
 * No answer / Call ended · mm:ss / Can't reach.
 */
export function describeCall(
  status: SoftphoneStatus,
  opts: { elapsedSeconds: number; callDurationSeconds: number | null }
): CallDisplay {
  const base = {
    detail: undefined as string | undefined,
    showMute: false,
    showHangup: false,
    showAnswer: false
  };

  switch (status) {
    case "incoming":
      return {
        ...base,
        statusLine: "Incoming call",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showAnswer: true
      };
    case "connecting":
      return {
        ...base,
        statusLine: "Ringing…",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showHangup: true
      };
    case "in-call":
      return {
        ...base,
        statusLine: formatDuration(opts.elapsedSeconds),
        tone: "active",
        isLive: true,
        isTerminal: false,
        showMute: true,
        showHangup: true
      };
    case "reconnecting":
      return {
        ...base,
        statusLine: "Reconnecting…",
        tone: "active",
        isLive: true,
        isTerminal: false,
        showMute: true,
        showHangup: true
      };
    case "ending":
      return {
        ...base,
        statusLine: "Ending…",
        tone: "active",
        isLive: true,
        isTerminal: false
      };
    case "no-answer":
      return {
        ...base,
        statusLine: "No answer",
        tone: "ended",
        isLive: false,
        isTerminal: true
      };
    case "call-ended":
      return {
        ...base,
        statusLine: `Call ended · ${formatDuration(opts.callDurationSeconds ?? 0)}`,
        tone: "ended",
        isLive: false,
        isTerminal: true
      };
    case "unreachable":
      return {
        ...base,
        statusLine: "Can't reach this number",
        detail:
          "Check the number and try again, or contact support if the issue persists.",
        tone: "error",
        isLive: false,
        isTerminal: true
      };
    default:
      return {
        ...base,
        statusLine: "",
        tone: "active",
        isLive: true,
        isTerminal: false
      };
  }
}
